export const queryKeys = Object.freeze({
  users: {
    all: ['users'] as const,
    search: (...search: string[]) => ['users', ...search] as const,
    me: () => [...queryKeys.users.all, 'me'] as const,
  },

  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.projects.lists(), { filters }] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.projects.details(), id] as const,
    members: (id: string) => [...queryKeys.projects.detail(id), 'members'] as const,
  },

  podcasts: {
    all: ['podcasts'] as const,
    lists: () => [...queryKeys.podcasts.all, 'list'] as const,
    list: (projectId: string, page: number) =>
      [...queryKeys.podcasts.lists(), { projectId, page }] as const,
    details: () => [...queryKeys.podcasts.all, 'detail'] as const,
    detail: (projectId: string, podcastId: string) =>
      [...queryKeys.podcasts.details(), projectId, podcastId] as const,
    configuration: (projectId: string, podcastId: string) =>
      [...queryKeys.podcasts.details(), projectId, podcastId, 'config'] as const,
    options: (projectId: string) => [...queryKeys.podcasts.all, projectId, 'options'] as const,
  },

  episodes: {
    all: ['episodes'] as const,
    lists: () => [...queryKeys.episodes.all, 'list'] as const,
    list: (podcastId: string, page: number) =>
      [...queryKeys.episodes.lists(), { podcastId, page }] as const,
    details: () => [...queryKeys.episodes.all, 'detail'] as const,
    detail: (projectId: string, podcastId: string, episodeId: string) =>
      [...queryKeys.episodes.details(), projectId, podcastId, episodeId] as const,
  },

  explore: {
    all: ['explore'] as const,
    projects: () => [...queryKeys.explore.all, 'projects'] as const,
  },

  voices: {
    all: ['voices'] as const,
    list: (projectId: string, podcastId: string, episodeId: string) =>
      [...queryKeys.voices.all, 'list', projectId, podcastId, episodeId] as const,
  },

  musics: {
    all: ['musics'] as const,
    list: () => [...queryKeys.voices.all, 'list'] as const,
    find: (projectId: string, podcastId: string, episodeId: string) =>
      [...queryKeys.voices.all, 'associated', projectId, podcastId, episodeId] as const,
  },

  hosts: {
    list: (projectId: string, podcastId: string, episodeId: string) =>
      ['hosts', projectId, podcastId, episodeId] as const,
  },

  scripts: {
    all: ['scripts'] as const,
    lists: () => [...queryKeys.scripts.all, 'list'] as const,
    list: (episodeId: string) => [...queryKeys.scripts.lists(), episodeId] as const,
    contextConfiguration: (projectId: string, podcastId: string, episodeId: string) =>
      [...queryKeys.scripts.all, 'context', projectId, podcastId, episodeId] as const,
  },

  audio: {
    find: (projectId: string, podcastId: string, episodeId: string) =>
      ['audio', projectId, podcastId, episodeId] as const,
    status: (projectId: string, podcastId: string, episodeId: string) =>
      ['audio', 'status', projectId, podcastId, episodeId] as const,
  },
});

export const mutationKeys = Object.freeze({
  projects: {
    all: ['projects', 'mutations'] as const,
    create: () => [...mutationKeys.projects.all, 'create'],
    update: (projectId: string) => [...mutationKeys.projects.all, 'update', projectId] as const,
    delete: (projectId: string) => [...mutationKeys.projects.all, 'delete', projectId] as const,
    updateRole: () => [...mutationKeys.projects.all, 'updateRole'],
    requestAccess: () => [...mutationKeys.projects.all, 'requestAccess'] as const,
    removeMember: (projectId: string) =>
      [...mutationKeys.projects.all, 'removeMember', projectId] as const,
    approveRequest: (projectId: string) =>
      [...mutationKeys.projects.all, 'approveRequest', projectId] as const,
    rejectRequest: (projectId: string) =>
      [...mutationKeys.projects.all, 'rejectRequest', projectId] as const,
    inviteMember: (projectId: string) =>
      [...mutationKeys.projects.all, 'inviteMember', projectId] as const,
  },

  podcasts: {
    all: ['podcasts', 'mutations'] as const,
    create: (projectId: string) => [...mutationKeys.podcasts.all, 'create', projectId] as const,
    delete: (projectId: string) => [...mutationKeys.podcasts.all, 'delete', projectId] as const,
    update: (projectId: string) => [...mutationKeys.podcasts.all, 'update', projectId] as const,
    configure: (projectId: string) =>
      [...mutationKeys.podcasts.all, 'configure', projectId] as const,
  },

  episodes: {
    all: ['episodes', 'mutations'] as const,
    create: (projectId: string, podcastId: string) =>
      [...mutationKeys.episodes.all, 'create', projectId, podcastId] as const,
    delete: (projectId: string, podcastId: string) =>
      [...mutationKeys.episodes.all, 'delete', projectId, podcastId] as const,
    update: (projectId: string, podcastId: string) =>
      [...mutationKeys.episodes.all, 'update', projectId, podcastId] as const,
  },

  music: {
    all: ['musics', 'mutations'] as const,
    create: (projectId: string, podcastId: string, episodeId: string) =>
      [...mutationKeys.music.all, 'create', projectId, podcastId, episodeId] as const,
    associate: (projectId: string, podcastId: string, episodeId: string) =>
      [...mutationKeys.music.all, 'associate', projectId, podcastId, episodeId] as const,
    upload: () => [...mutationKeys.music.all, 'upload'] as const,
  },

  hosts: {
    all: ['hosts', 'mutations'] as const,
    update: (projectId: string, podcastId: string, episodeId: string) =>
      ['hosts', 'mutations', 'update', projectId, podcastId, episodeId] as const,
    create: (projectId: string, podcastId: string, episodeId: string) =>
      ['hosts', 'mutations', 'create', projectId, podcastId, episodeId] as const,
  },

  scripts: {
    all: ['scripts', 'mutations'] as const,
    create: (projectId: string, podcastId: string, episodeId: string) =>
      ['scripts', 'mutations', 'create', projectId, podcastId, episodeId] as const,
    generate: (projectId: string, podcastId: string, episodeId: string) =>
      ['scripts', 'mutations', 'generate', projectId, podcastId, episodeId] as const,
    update: (projectId: string, podcastId: string, episodeId: string) =>
      ['scripts', 'mutations', 'update', projectId, podcastId, episodeId] as const,
    voices: (projectId: string, podcastId: string, episodeId: string) =>
      ['scripts', 'mutations', 'voices', projectId, podcastId, episodeId] as const,
    files: (projectId: string, podcastId: string, episodeId: string) =>
      ['scripts', 'mutations', 'files', projectId, podcastId, episodeId] as const,
  },

  audio: {
    all: ['audio', 'mutations'] as const,
    create: (projectId: string, podcastId: string, episodeId: string) =>
      ['audio', 'mutations', 'create', projectId, podcastId, episodeId] as const,
    save: (projectId: string, podcastId: string, episodeId: string) =>
      ['audio', 'mutations', 'save', projectId, podcastId, episodeId] as const,
  },

  publish: {
    all: ['publish', 'mutations'] as const,
    episode: (projectId: string, podcastId: string, episodeId: string) =>
      ['publish', 'mutations', 'episode', projectId, podcastId, episodeId] as const,
  },
});
